// Sheet (009 revision 6, FR-044, FR-046, FR-048; shadcn/ui `sheet`, restyled): a panel that slides over the page
// from an edge, on Radix Dialog (focus kept inside, Escape and the close button close it, focus returns). Sides are
// logical: `end` and `start` follow the reading direction; `bottom` is the reader app's bottom sheet below 768 px.
// The shell's collapsed navigation and the staff detail panels are sheets. The panel scrolls inside itself
// (`data-ui-scroll="dialog"`).
// 010 revision 5: the panel is marked `aria-modal`, so the page behind it counts as inert.
import { cva, type VariantProps } from 'class-variance-authority';
import { Dialog as SheetPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { CloseIcon } from '../../icons';
import { cn } from '../../lib/cn';
import { DialogOverlay } from './dialog';

/**
 * The sheet's root (open state).
 * @param props Radix Dialog root props
 * @returns the root
 */
export function Sheet(props: ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

/**
 * The button that opens the sheet (use `asChild` with a {@link Button}).
 * @param props Radix Dialog trigger props
 * @returns the trigger
 */
export function SheetTrigger(props: ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

/**
 * A control that closes the sheet (use `asChild`).
 * @param props Radix Dialog close props
 * @returns the control
 */
export function SheetClose(props: ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

/** The panel's classes by side. */
export const sheetVariants = cva(
  'fixed z-50 flex flex-col gap-4 overflow-y-auto bg-surface p-4 text-ink shadow-pop',
  {
    variants: {
      side: {
        end: 'inset-y-0 end-0 h-full w-full max-w-sm border-s border-line',
        start: 'inset-y-0 start-0 h-full w-full max-w-sm border-e border-line',
        top: 'inset-x-0 top-0 max-h-sheet border-b border-line',
        bottom: 'inset-x-0 bottom-0 max-h-sheet rounded-t-xl border-t border-line',
      },
    },
    defaultVariants: { side: 'end' },
  },
);

/** Props of {@link SheetContent}. */
export type SheetContentProps = ComponentProps<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants> & {
    /** Show the close button at the top end (default true). */
    showCloseButton?: boolean;
    /** The close button's accessible name (default "Close"; pass the translation). */
    closeLabel?: string;
    /** Where the sheet is rendered (default: the body). */
    container?: HTMLElement | null;
  };

/**
 * The sheet's panel, with the scrim and the close button.
 * @param props the side, close button, container and Radix Dialog content props
 * @returns the panel
 */
export function SheetContent({
  className,
  children,
  side = 'end',
  showCloseButton = true,
  closeLabel = 'Close',
  container,
  ...props
}: SheetContentProps) {
  return (
    <SheetPrimitive.Portal container={container}>
      <DialogOverlay data-slot="sheet-overlay" />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        aria-modal="true"
        data-side={side}
        data-ui-scroll="dialog"
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            aria-label={closeLabel}
            className="absolute end-2 top-2 inline-grid size-control-touch cursor-pointer place-items-center rounded-lg text-ink-secondary hover:bg-subtle hover:text-ink lg:size-control-sm"
          >
            <CloseIcon size={16} />
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

/**
 * The sheet's header: title and description (kept clear of the close button).
 * @param props the div's attributes
 * @returns the header
 */
export function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      data-ui-density="slot"
      className={cn('flex flex-col gap-2 pe-close', className)}
      {...props}
    />
  );
}

/**
 * The sheet's footer: its actions, at the bottom of the panel.
 * @param props the div's attributes
 * @returns the footer
 */
export function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      data-ui-part="group"
      className={cn('mt-auto flex flex-col gap-2', className)}
      {...props}
    />
  );
}

/**
 * The sheet's title (its accessible name).
 * @param props Radix Dialog title props
 * @returns the title
 */
export function SheetTitle({ className, ...props }: ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('font-display text-lg font-semibold wrap-anywhere', className)}
      {...props}
    />
  );
}

/**
 * The sheet's description (its accessible description).
 * @param props Radix Dialog description props
 * @returns the description
 */
export function SheetDescription({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-ink-secondary wrap-anywhere', className)}
      {...props}
    />
  );
}
